import { useLocalStorage } from "@/composables/useLocalStorage";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export type FavoriteType = "degree" | "article";

export type FavoriteItem = {
  slug: string;
  url: string;
  title: string;
  metaDescription?: string;
  image?: string;
  type: FavoriteType;
  createdAt: string;
};

export type FavoriteInput = Omit<FavoriteItem, "createdAt"> & {
  createdAt?: string;
};

const FAVORITES_STORAGE_KEY = "favorites";

export const useFavoriteStore = defineStore("favorite", () => {
  const items = ref<FavoriteItem[]>([]);
  const storeLoading = ref<boolean>(true);

  const hasItems = computed(() => items.value.length > 0);
  const degreeFavorites = computed(() => items.value.filter((item) => item.type === "degree"));
  const articleFavorites = computed(() => items.value.filter((item) => item.type === "article"));

  const saveItems = async () => {
    if (!import.meta.client) {
      return;
    }

    const localStorage = useLocalStorage();
    await localStorage.setItem(FAVORITES_STORAGE_KEY, items.value);
  };

  const isFavorited = (slug: string, type?: FavoriteType) => {
    return items.value.some((item) => {
      return item.slug === slug && (!type || item.type === type);
    });
  };

  const addFavorite = async (favorite: FavoriteInput) => {
    if (isFavorited(favorite.slug, favorite.type)) {
      return (
        items.value.find((item) => item.slug === favorite.slug && item.type === favorite.type) ||
        null
      );
    }

    const nextFavorite: FavoriteItem = {
      ...favorite,
      createdAt: favorite.createdAt || new Date().toISOString()
    };

    items.value = [nextFavorite, ...items.value];
    await saveItems();

    return nextFavorite;
  };

  const removeFavorite = async (slug: string, type?: FavoriteType) => {
    items.value = items.value.filter((item) => {
      return item.slug !== slug || Boolean(type && item.type !== type);
    });
    await saveItems();
  };

  const toggleFavorite = async (favorite: FavoriteInput) => {
    if (isFavorited(favorite.slug, favorite.type)) {
      await removeFavorite(favorite.slug, favorite.type);
      return false;
    }

    await addFavorite(favorite);
    return true;
  };

  const clearFavorites = async () => {
    items.value = [];

    if (!import.meta.client) {
      return;
    }

    const localStorage = useLocalStorage();
    await localStorage.removeItem(FAVORITES_STORAGE_KEY);
  };

  const initState = async () => {
    if (!import.meta.client) {
      storeLoading.value = false;
      return;
    }

    const localStorage = useLocalStorage();
    const storedItems = await localStorage.getItem<FavoriteItem[]>(FAVORITES_STORAGE_KEY);

    items.value = Array.isArray(storedItems) ? storedItems : [];
    storeLoading.value = false;
  };

  return {
    items,
    storeLoading,
    hasItems,
    degreeFavorites,
    articleFavorites,
    isFavorited,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    clearFavorites,
    initState
  };
});
